/* eslint-disable no-promise-executor-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import type { Channel } from 'redux-saga';
import {
 actionChannel,
 call,
 delay,
 fork,
 put,
 select,
 take,
 takeLatest,
} from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling } from '../../api/errorHandling';

import {
 getConfigStorageItem,
 setConfigStorageItem,
} from '../../hooks/useConfigStorage';
import { getCookieCustom } from '../../utils/cookies';
import { isJson } from '../../utils/dataTypes';
import { RootState } from '../store';
import { ClientConfigurationItem } from './clientConfigurationSlice';
import {
 createClientConfigurationFailure,
 createClientConfigurationFinish,
 createClientConfigurationStart,
 createClientConfigurationSuccess,
 deleteClientConfigurationFailure,
 deleteClientConfigurationFinish,
 deleteClientConfigurationStart,
 deleteClientConfigurationSuccess,
 getClientConfigurationFailure,
 getClientConfigurationFinish,
 getClientConfigurationSelfFailure,
 getClientConfigurationSelfStart,
 getClientConfigurationSelfSuccess,
 getClientConfigurationsFailure,
 getClientConfigurationsFinish,
 getClientConfigurationsStart,
 getClientConfigurationsSuccess,
 getClientConfigurationStart,
 getClientConfigurationSuccess,
 clientSettingSaveResult,
 loadClientSettingFailure,
 loadClientSettingStart,
 loadClientSettingSuccess,
 saveClientSettingFailure,
 saveClientSettingStart,
 saveClientSettingSuccess,
 updateClientConfigurationFailure,
 updateClientConfigurationFinish,
 updateClientConfigurationStart,
 updateClientConfigurationSuccess,
} from './clientConfigurationSlice';
import { findByParameter, readSettingValue } from './configValues';

const getConfigByName = (name: string, configurationObj: any) => {
 if (typeof configurationObj !== 'object' || configurationObj === null)
  return false;

 const confVal = configurationObj.find(
  (conf: any) => conf.Parameter === name,
 )?.Value;
 //  console.log({ confVal, a: JSON.parse(confVal), name, configurationObj });
 if (isJson(confVal)) {
  return JSON.parse(confVal);
 }
 return false;
};

const mergeClientConfigurationItem = (
 list: ClientConfigurationItem[] | null | undefined,
 item: ClientConfigurationItem | null | undefined,
): ClientConfigurationItem[] => {
 const current = Array.isArray(list) ? list : [];
 if (!item || typeof item !== 'object') return current;
 const byId = item.Id
  ? current.findIndex((c) => c.Id === item.Id)
  : -1;
 if (byId >= 0) {
  const next = current.slice();
  next[byId] = { ...current[byId], ...item };
  return next;
 }
 const byParameter = item.Parameter
  ? current.findIndex((c) => c.Parameter === item.Parameter)
  : -1;
 if (byParameter >= 0) {
  const next = current.slice();
  next[byParameter] = { ...current[byParameter], ...item };
  return next;
 }
 return [...current, item];
};

function* fetchClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.get,
   `/ClientConfiguration/v1/${props.payload}`,
  );
  yield put(
   getClientConfigurationSuccess({
    data: response.data || [],
    rowCount: response.headers['x-total-count'] || 123,
    // Left untranslated on purpose: none of this slice's Success actions are
    // in src/redux/toast/watchList.ts, so these strings never render. Add a
    // toasts.* key and i18n.t() here if one is ever watched.
    message: 'ClientConfiguration loaded successfully',
    type: 'success',
   }),
  );
  yield delay(0); // ???
  yield put(getClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, getClientConfigurationFailure);
 }
}

function* fetchClientConfigurations(props: any) {
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.post,
   `/ClientConfiguration/v1`,
   {},
  );
  yield put(
   getClientConfigurationsSuccess({
    message: 'ClientConfiguration loaded successfully',
    type: 'success',
    clientConfigurations: response.data || [],
    data: response.data || [],
    rowCount: response.headers['x-total-count'] || 123,
   }),
  );
  yield delay(0);
  yield put(getClientConfigurationsFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, getClientConfigurationsFailure);
 }
}

const readCachedConfiguration = (): ClientConfigurationItem[] => {
 const raw = getConfigStorageItem('configuration');
 if (Array.isArray(raw)) return raw;
 if (typeof raw === 'string') {
  try {
   const parsed = JSON.parse(raw);
   return Array.isArray(parsed) ? parsed : [];
  } catch {
   return [];
  }
 }
 return [];
};

const writeCachedConfiguration = (list: ClientConfigurationItem[]) => {
 setConfigStorageItem({ key: 'configuration', value: JSON.stringify(list) });
};

// Set once this page-load has sent one real GET /self, so an admin's preset
// change reaches the user on their next reload instead of staying hidden
// behind the (otherwise long-lived) localStorage cache (AGENTS.md rule 3).
// Every later call in the same session may use `cached` as asked.
let hasFetchedSelfConfigThisSession = false;

export const resetSelfConfigSessionCacheForTests = () => {
 hasFetchedSelfConfigThisSession = false;
};

function* fetchClientConfigurationSelf(props: any) {
 // Read outside the try so the catch below can still serve it when the
 // forced-fresh GET below throws.
 const configurationObj = readCachedConfiguration();
 try {
  const forceFresh = !hasFetchedSelfConfigThisSession;
  if (forceFresh || !props.payload.cached || configurationObj.length === 0) {
   // Set only once the fresh response has actually landed and been stored
   // (below), not here: `takeLatest` cancels an in-flight call of this saga
   // when another `getClientConfigurationSelfStart` fires before this GET
   // resolves (Search.tsx and useTableInit.ts both dispatch `{cached: true}`
   // on the same page). Flipping the flag before the `yield call` let a
   // cancelled attempt still mark the session "fetched fresh", so the
   // surviving dispatch served a stale `configuration` cache instead of
   // retrying — the same as a failed GET, which must retry too.
   const response: AxiosResponse<any[]> = yield call(
    api.get,
    `/ClientConfiguration/v1/self`,
   );
   yield put(
    getClientConfigurationSelfSuccess({
     message: 'ClientConfiguration loaded successfully',
     type: 'success',
     clientConfigurations: response.data || [],
     presetsColumns: getConfigByName('columns', response.data),
     fieldPresets: getConfigByName('fields', response.data),
     data: response.data || [],
     rowCount: response.headers['x-total-count'] || 123,
    }),
   );
   writeCachedConfiguration((response.data || []) as ClientConfigurationItem[]);
   hasFetchedSelfConfigThisSession = true;
  } else {
   yield put(
    getClientConfigurationSelfSuccess({
     message: 'ClientConfiguration loaded successfully',
     type: 'success',
     clientConfigurations: configurationObj,
     presetsColumns: getConfigByName('columns', configurationObj),
     fieldPresets: getConfigByName('fields', configurationObj),
     data: configurationObj,
     rowCount: configurationObj.length,
    }),
   );
  }
 } catch (error: unknown) {
  yield* errorHandling(error, getClientConfigurationSelfFailure);
  // The forced-fresh GET failed: fall back to the cache instead of leaving
  // presetsColumns null for the rest of the session. `hasFetchedSelfConfigThisSession`
  // stays unset (never reached the line above), so the next dispatch tries
  // the server again rather than treating this session as already fresh.
  if (configurationObj.length > 0) {
   yield put(
    getClientConfigurationSelfSuccess({
     message: 'ClientConfiguration loaded successfully',
     type: 'success',
     clientConfigurations: configurationObj,
     presetsColumns: getConfigByName('columns', configurationObj),
     fieldPresets: getConfigByName('fields', configurationObj),
     data: configurationObj,
     rowCount: configurationObj.length,
     // useTableInit waits for fresh presets before its once-per-load
     // columns reconciliation.
     stale: true,
    }),
   );
  }
 }
}
function* createClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<ClientConfigurationItem> = yield call(
   api.post,
   `/ClientConfiguration/v1/create`,
   props.payload.data,
  );

  const currentList: ClientConfigurationItem[] | null = yield select(
   (state: RootState) => state.clientConfiguration.clientConfigurations,
  );
  const nextList = mergeClientConfigurationItem(currentList, response.data);

  writeCachedConfiguration(nextList);

  yield put(
   createClientConfigurationSuccess({
    data: response.data,
    clientConfigurations: nextList,
    presetsColumns: getConfigByName('columns', nextList),
    fieldPresets: getConfigByName('fields', nextList),
   }),
  );
  yield delay(0);
  yield put(createClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, createClientConfigurationFailure);
 }
}

function* updateClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<ClientConfigurationItem> = yield call(
   api.put,
   `/ClientConfiguration/v1/update/${props.payload.id}`,
   props.payload.data,
  );

  const currentList: ClientConfigurationItem[] | null = yield select(
   (state: RootState) => state.clientConfiguration.clientConfigurations,
  );
  const fromResponse =
   response.data &&
   typeof response.data === 'object' &&
   (response.data as ClientConfigurationItem).Parameter
    ? (response.data as ClientConfigurationItem)
    : null;
  const nextItem: ClientConfigurationItem =
   fromResponse ||
   ({
    Id: props.payload.id,
    ...props.payload.data,
   } as ClientConfigurationItem);
  const nextList = mergeClientConfigurationItem(currentList, nextItem);

  writeCachedConfiguration(nextList);

  yield put(
   updateClientConfigurationSuccess({
    data: response.data,
    clientConfigurations: nextList,
    presetsColumns: getConfigByName('columns', nextList),
    fieldPresets: getConfigByName('fields', nextList),
   }),
  );
  yield delay(0);
  yield put(updateClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, updateClientConfigurationFailure);
 }
}

function* deleteClientConfiguration(props: any) {
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `/ClientConfiguration/v1/delete/${props.payload.id}`,
   props.payload,
  );

  yield put(
   deleteClientConfigurationSuccess({
    data: response.data,
    // rowCount: response.headers['x-total-count'] || 123,
   }),
  );
  yield delay(0); // ???
  yield put(deleteClientConfigurationFinish(null));
 } catch (error: unknown) {
  yield* errorHandling(error, deleteClientConfigurationFailure);
 }
}

// GET /self answers 204 with an empty body when the user has no rows.
const fetchSelfList = async (): Promise<ClientConfigurationItem[]> => {
 const response: AxiosResponse<unknown> = await api.get(
  '/ClientConfiguration/v1/self',
 );
 return Array.isArray(response.data)
  ? (response.data as ClientConfigurationItem[])
  : [];
};

export function* loadClientSetting(
 action: PayloadAction<{ parameter: string; requestId: string }>,
) {
 const { parameter, requestId } = action.payload;
 try {
  const list: ClientConfigurationItem[] = yield call(fetchSelfList);
  writeCachedConfiguration(list);
  yield put(
   loadClientSettingSuccess({
    parameter,
    requestId,
    value: readSettingValue(list, parameter),
    clientConfigurations: list,
   }),
  );
 } catch (error: unknown) {
  yield* errorHandling(error, (payload) =>
   loadClientSettingFailure({ ...payload, parameter, requestId }),
  );
 }
}

export function* saveClientSetting(
 action: PayloadAction<{ parameter: string; value: unknown; saveId?: string }>,
) {
 const { parameter, value, saveId } = action.payload;
 try {
  const cached: ClientConfigurationItem[] | null = yield select(
   (state: RootState) => state.clientConfiguration.clientConfigurations,
  );
  let list: ClientConfigurationItem[] = cached ?? [];
  let row = findByParameter(list, parameter);
  if (!row) {
   // The list in state can predate a row this user created on another
   // device; creating blindly would leave two rows for one parameter.
   list = yield call(fetchSelfList);
   row = findByParameter(list, parameter);
  }
  const data = {
   Name: getCookieCustom('user_id') || '-1',
   Parameter: parameter,
   Type: 'string',
   Value: JSON.stringify(value),
  };
  let response: AxiosResponse<unknown>;
  if (row) {
   response = yield call(
    api.put,
    `/ClientConfiguration/v1/update/${row.Id}`,
    data,
   );
  } else {
   response = yield call(api.post, '/ClientConfiguration/v1/create', data);
  }
  const fromResponse =
   response.data &&
   typeof response.data === 'object' &&
   (response.data as ClientConfigurationItem).Id
    ? (response.data as ClientConfigurationItem)
    : null;
  const saved = fromResponse ?? (row ? { ...row, ...data } : null);
  // Re-read state instead of reusing the pre-request `list`: another save
  // (e.g. a column preset) can have committed to state while this request
  // was in flight, and merging into the stale snapshot would drop it.
  const current: ClientConfigurationItem[] | null = yield select(
   (state: RootState) => state.clientConfiguration.clientConfigurations,
  );
  const base = current ?? list;
  const nextList = saved ? mergeClientConfigurationItem(base, saved) : base;
  writeCachedConfiguration(nextList);
  yield put(saveClientSettingSuccess({ clientConfigurations: nextList }));
  yield put(clientSettingSaveResult({ parameter, saveId, ok: true }));
 } catch (error: unknown) {
  // saveClientSettingFailure passed unwrapped: errorHandling matches the
  // watchList callback by reference, and a wrapper here would toast twice.
  yield* errorHandling(error, saveClientSettingFailure);
  yield put(clientSettingSaveResult({ parameter, saveId, ok: false }));
 }
}

type ClientSettingAction =
 | PayloadAction<{ parameter: string; value: unknown; saveId?: string }>
 | PayloadAction<{ parameter: string; requestId: string }>;

// One save or load at a time, in order: a create must finish (and its Id
// reach state) before the next save of that parameter, or both would create
// a row; and a load queued behind a save (dashboard remount right after the
// unmount flush) must read what that save wrote.
function* watchClientSetting() {
 const channel: Channel<ClientSettingAction> = yield actionChannel([
  saveClientSettingStart.type,
  loadClientSettingStart.type,
 ]);
 while (true) {
  const action: ClientSettingAction = yield take(channel);
  if (saveClientSettingStart.match(action)) {
   yield call(saveClientSetting, action);
  } else if (loadClientSettingStart.match(action)) {
   yield call(loadClientSetting, action);
  }
 }
}

export default function* watchClientConfigurationSaga() {
 yield takeLatest(getClientConfigurationStart.type, fetchClientConfiguration);
 yield takeLatest(getClientConfigurationsStart.type, fetchClientConfigurations);
 yield takeLatest(
  getClientConfigurationSelfStart.type,
  fetchClientConfigurationSelf,
 );
 yield takeLatest(
  createClientConfigurationStart.type,
  createClientConfiguration,
 );
 yield takeLatest(
  updateClientConfigurationStart.type,
  updateClientConfiguration,
 );
 yield takeLatest(
  deleteClientConfigurationStart.type,
  deleteClientConfiguration,
 );
 yield fork(watchClientSetting);
}
